import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, Search, Mail, Phone, MapPin, Calendar, 
  Briefcase, Star, TrendingUp, Clock, ChevronRight,
  User, Building, Target, Award, Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MyTeam = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, usersRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/users`)
      ]);
      
      // For managers: show teams they are assigned to OR manage
      // Get user's team_ids
      const userTeamIds = user?.team_ids || [];
      
      const myTeams = teamsRes.data.filter(team => 
        // User is the manager of this team
        team.manager_id === user?.id || 
        team.manager_email === user?.email ||
        // User is assigned to this team (via team_ids)
        userTeamIds.includes(team.id) ||
        // Super admin/admin can see all
        user?.role === 'super_admin' ||
        user?.role === 'admin'
      );
      
      setTeams(myTeams);
      setAllUsers(usersRes.data);
      
      // Auto-select first team if available
      if (myTeams.length > 0) {
        setSelectedTeam(myTeams[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Get team members - users who have this team in their team_ids
  const getTeamMembers = (team) => {
    if (!team) return [];
    return allUsers.filter(u => {
      const userTeamIds = u.team_ids || [];
      return userTeamIds.includes(team.id) && u.id !== user?.id;
    });
  };

  const filteredMembers = selectedTeam 
    ? getTeamMembers(selectedTeam).filter(member =>
        `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.role?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setMemberDialogOpen(true);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'sales': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'sales_rep': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'sales_manager': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'production': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'production_manager': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
      'operations': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'operations_manager': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
      'admin': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'super_admin': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[role] || 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200';
  };

  const formatRole = (role) => {
    return role?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Unknown';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">My Team</h1>
            <p className="text-muted-foreground mt-1">
              Manage and view your team members
            </p>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            <Users className="h-5 w-5 mr-2" />
            {teams.reduce((acc, team) => acc + getTeamMembers(team).length, 0)} Total Members
          </Badge>
        </div>

        {teams.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Teams Assigned</h3>
              <p className="text-muted-foreground text-center max-w-md">
                You do not have any teams assigned to you yet. Contact your administrator to be assigned as a team manager.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Teams List */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Your Teams</CardTitle>
                  <CardDescription>{teams.length} team(s) assigned</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {teams.map((team) => (
                    <button
                      key={team.id}
                      onClick={() => setSelectedTeam(team)}
                      className={`w-full p-3 rounded-lg text-left transition-all ${
                        selectedTeam?.id === team.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            selectedTeam?.id === team.id 
                              ? 'bg-white/20' 
                              : 'bg-primary/10'
                          }`}>
                            <Users className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-semibold">{team.name}</p>
                            <p className={`text-xs ${
                              selectedTeam?.id === team.id 
                                ? 'text-primary-foreground/70' 
                                : 'text-muted-foreground'
                            }`}>
                              {getTeamMembers(team).length} members
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Team Members */}
            <div className="lg:col-span-3 space-y-4">
              {selectedTeam && (
                <>
                  {/* Team Header */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-xl flex items-center gap-2">
                            <Building className="h-5 w-5 text-primary" />
                            {selectedTeam.name}
                          </CardTitle>
                          <CardDescription className="mt-1">
                            {selectedTeam.description || 'No description'}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search members..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="pl-9 w-64"
                            />
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Members Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredMembers.length === 0 ? (
                      <Card className="col-span-full">
                        <CardContent className="flex flex-col items-center justify-center py-12">
                          <User className="h-12 w-12 text-muted-foreground mb-3" />
                          <p className="text-muted-foreground">
                            {searchTerm ? 'No members match your search' : 'No members in this team'}
                          </p>
                        </CardContent>
                      </Card>
                    ) : (
                      filteredMembers.map((member) => (
                        <Card 
                          key={member.id} 
                          className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50"
                          onClick={() => handleMemberClick(member)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <Avatar className="h-14 w-14 border-2 border-primary/20">
                                <AvatarImage src={member.avatar_url} />
                                <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-white text-lg font-semibold">
                                  {member.first_name?.[0]}{member.last_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold truncate">
                                  {member.first_name} {member.last_name}
                                </h3>
                                <p className="text-sm text-muted-foreground truncate">
                                  {member.email}
                                </p>
                                <Badge className={`mt-2 text-xs ${getRoleBadgeColor(member.role)}`}>
                                  {formatRole(member.role)}
                                </Badge>
                              </div>
                            </div>
                            
                            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-2 text-xs">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                <span className="truncate">{member.phone || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                <span className="truncate">{member.market || 'N/A'}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Member Detail Dialog */}
        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
          <DialogContent className="max-w-2xl">
            {selectedMember && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={selectedMember.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-white text-lg">
                        {selectedMember.first_name?.[0]}{selectedMember.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span className="text-xl">{selectedMember.first_name} {selectedMember.last_name}</span>
                      <Badge className={`ml-3 ${getRoleBadgeColor(selectedMember.role)}`}>
                        {formatRole(selectedMember.role)}
                      </Badge>
                    </div>
                  </DialogTitle>
                  <DialogDescription>
                    Employee details and performance metrics
                  </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="details" className="mt-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="performance">Performance</TabsTrigger>
                    <TabsTrigger value="activity">Activity</TabsTrigger>
                  </TabsList>

                  <TabsContent value="details" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <Mail className="h-4 w-4 text-blue-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Email</p>
                            <p className="text-sm font-medium">{selectedMember.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <Phone className="h-4 w-4 text-green-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Phone</p>
                            <p className="text-sm font-medium">{selectedMember.phone || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <MapPin className="h-4 w-4 text-orange-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Market</p>
                            <p className="text-sm font-medium">{selectedMember.market || 'Not assigned'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <Briefcase className="h-4 w-4 text-purple-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Role</p>
                            <p className="text-sm font-medium">{formatRole(selectedMember.role)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <Calendar className="h-4 w-4 text-cyan-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Joined</p>
                            <p className="text-sm font-medium">
                              {selectedMember.created_at 
                                ? new Date(selectedMember.created_at).toLocaleDateString()
                                : 'Unknown'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                          <Clock className="h-4 w-4 text-pink-500" />
                          <div>
                            <p className="text-xs text-muted-foreground">Status</p>
                            <Badge variant={selectedMember.is_active ? 'default' : 'secondary'}>
                              {selectedMember.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="performance" className="space-y-4 mt-4">
                    <div className="grid grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="p-4 text-center">
                          <Target className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                          <p className="text-2xl font-bold">—</p>
                          <p className="text-xs text-muted-foreground">Goals Met</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-500" />
                          <p className="text-2xl font-bold">—</p>
                          <p className="text-xs text-muted-foreground">Sales</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <Award className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                          <p className="text-2xl font-bold">—</p>
                          <p className="text-xs text-muted-foreground">Rating</p>
                        </CardContent>
                      </Card>
                    </div>
                    <Card>
                      <CardContent className="flex items-center justify-center py-8">
                        <p className="text-muted-foreground">Performance metrics coming soon</p>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="activity" className="space-y-4 mt-4">
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-8">
                        <Activity className="h-12 w-12 text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Activity tracking coming soon</p>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setMemberDialogOpen(false)}>
                    Close
                  </Button>
                  <Button onClick={() => window.location.href = `mailto:${selectedMember.email}`}>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MyTeam;
